import { useCallback, useContext, useEffect, useRef } from 'react';
import api from 'api';
import { MessageType } from 'models/Message';
import MessagingContext from '../../../providers/Messaging/Messaging.context';
import useChatMessageIdentity from '../../../hooks/useChatMessageIdentity';
import { getHasElementsInArray } from 'utils';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { EMPTY_ARRAY } from 'data/global';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import {
  MessageCreatedEventData,
  ReplySuggestionsUpdatedEventData,
} from 'services/socketService/socketEventData';

type UseReplySuggestionsParams = {
  sessionId: string;
  lastMessage?: MessageType;
  disabled?: boolean;
};

export default function useReplySuggestions(params: UseReplySuggestionsParams) {
  const { sessionId, lastMessage, disabled } = params;

  const suggestionsShownRef = useRef(false);
  const selectedSuggestionRef = useRef<string>(null);

  const { currentSession, updateCurrentSession } = useContext(MessagingContext);
  const { currentUser } = useContext(CurrentUserContext);

  const { replySuggestions, isGeneratingReplySuggestions } =
    currentSession || {};

  const { isMyMessage } = useChatMessageIdentity(currentSession)(
    lastMessage ?? {},
  );

  const getMixpanelProps = useGetMixpanelProps();

  const lastMessageId = lastMessage?._id;

  const canSuggest =
    !!lastMessageId &&
    !disabled &&
    !isMyMessage &&
    !currentUser?.hideReplySuggestions;

  const suggestions = canSuggest
    ? replySuggestions ?? EMPTY_ARRAY
    : EMPTY_ARRAY;

  const hasSuggestions = getHasElementsInArray(suggestions);

  const loading =
    canSuggest && !hasSuggestions && !!isGeneratingReplySuggestions;

  const onMessageCreated = useCallback(
    ({ session }: MessageCreatedEventData) => {
      if (session?._id !== sessionId) return;

      updateCurrentSession({
        replySuggestions: session.replySuggestions,
        isGeneratingReplySuggestions: session.isGeneratingReplySuggestions,
      });
    },
    [sessionId, updateCurrentSession],
  );

  const onReplySuggestionsUpdated = useCallback(
    ({
      sessionId: updatedSessionId,
      replySuggestions,
      isGeneratingReplySuggestions,
    }: ReplySuggestionsUpdatedEventData) => {
      if (updatedSessionId !== sessionId) return;

      updateCurrentSession({
        replySuggestions,
        isGeneratingReplySuggestions,
      });
    },
    [sessionId, updateCurrentSession],
  );

  const removeSuggestions = useCallback(async () => {
    if (!hasSuggestions && !loading) return;

    updateCurrentSession({
      replySuggestions: [],
      isGeneratingReplySuggestions: false,
    });

    try {
      await api.messages.dismissReplySuggestions(sessionId);
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
    }
  }, [
    hasSuggestions,
    loading,
    sessionId,
    updateCurrentSession,
    getMixpanelProps,
  ]);

  const selectSuggestion = useCallback(
    (suggestion: string) => (selectedSuggestionRef.current = suggestion),
    [],
  );

  useEffect(() => {
    suggestionsShownRef.current = false;
    selectedSuggestionRef.current = null;
  }, [sessionId, lastMessageId]);

  useEffect(() => {
    if (hasSuggestions) suggestionsShownRef.current = true;
  }, [hasSuggestions, sessionId, lastMessageId]);

  useSocketEvent(LISTEN_EVENTS.MESSAGE_CREATED, onMessageCreated);
  useSocketEvent(
    LISTEN_EVENTS.REPLY_SUGGESTIONS_UPDATED,
    onReplySuggestionsUpdated,
  );

  return {
    suggestions,
    loading,
    suggestionsShown: suggestionsShownRef.current,
    selectedSuggestion: selectedSuggestionRef.current,
    selectSuggestion,
    removeSuggestions,
  };
}
